// Hierarchical Error Structure with HTTP Status Code Level Base Types

// Base Error Types (HTTP Status Code Level)
export type ValidationErrorType = 
  | 'invalid_email'
  | 'invalid_referral_code'
  | 'missing_password'
  | 'validation_missing'
  | 'payment_exceeding'
  | 'invalid_file_type'
  | 'file_too_large'
  | 'invalid_date_format'
  | 'exceed_attempt_limits';

export type AuthenticationErrorType =
  | 'no_api_key'
  | 'invalid_token'
  | 'token_expired'
  | 'wrong_credentials'
  | 'account_locked';

export type AuthorizationErrorType =
  | 'insufficient_permissions'
  | 'access_denied'
  | 'account_suspended'
  | 'feature_restricted';

export type ResourceErrorType =
  | 'user_not_found'
  | 'lead_not_found'
  | 'agent_not_found'
  | 'conversation_not_found'
  | 'ticket_not_found';

export type ConflictErrorType =
  | 'email_already_registered'
  | 'user_already_registered'
  | 'duplicate_lead'
  | 'resource_in_use'
  | 'concurrent_modification';

export type BusinessErrorType =
  | 'insufficient_credits'
  | 'subscription_required'
  | 'payment_failed'
  | 'quota_exceeded';

export type RateLimitErrorType =
  | 'too_many_requests'
  | 'daily_quota_reached'
  | 'connection_limit';

export type ExternalServiceErrorType =
  | 'stripe_service_down'
  | 'email_service_failed'
  | 's3_upload_failed'
  | 'webhook_delivery_failed';

export type SystemErrorType =
  | 'database_connection_failed'
  | 'cache_service_down'
  | 'internal_processing_error'
  | 'service_unavailable';

// Union type for all error subtypes
export type ErrorSubtype = 
  | ValidationErrorType
  | AuthenticationErrorType
  | AuthorizationErrorType
  | ResourceErrorType
  | ConflictErrorType
  | BusinessErrorType
  | RateLimitErrorType
  | ExternalServiceErrorType
  | SystemErrorType;

// HTTP Status Code Mapping
export const HTTP_STATUS_CODES = {
  VALIDATION_ERROR: 400,
  AUTHENTICATION_ERROR: 401,
  BUSINESS_ERROR: 402,
  AUTHORIZATION_ERROR: 403,
  RESOURCE_ERROR: 404,
  CONFLICT_ERROR: 409,
  RATE_LIMIT_ERROR: 429,
  EXTERNAL_SERVICE_ERROR: 424,
  SYSTEM_ERROR: 500,
} as const;

// Base Error Interface
export interface BaseError {
  subtype: ErrorSubtype;
  message: string;
  httpStatusCode: number;
  timestamp?: string;
  details?: Record<string, any>;
}

// Specific Error Types
export interface ValidationError extends BaseError {
  subtype: ValidationErrorType;
  httpStatusCode: 400;
}

export interface AuthenticationError extends BaseError {
  subtype: AuthenticationErrorType;
  httpStatusCode: 401;
}

export interface AuthorizationError extends BaseError {
  subtype: AuthorizationErrorType;
  httpStatusCode: 403;
}

export interface ResourceError extends BaseError {
  subtype: ResourceErrorType;
  httpStatusCode: 404;
}

export interface ConflictError extends BaseError {
  subtype: ConflictErrorType;
  httpStatusCode: 409;
}

export interface BusinessError extends BaseError {
  subtype: BusinessErrorType;
  httpStatusCode: 402;
}

export interface RateLimitError extends BaseError {
  subtype: RateLimitErrorType;
  httpStatusCode: 429;
}

export interface ExternalServiceError extends BaseError {
  subtype: ExternalServiceErrorType;
  httpStatusCode: 424;
}

export interface SystemError extends BaseError {
  subtype: SystemErrorType;
  httpStatusCode: 500;
}

// Union type for all specific errors
export type AppError = 
  | ValidationError
  | AuthenticationError
  | AuthorizationError
  | ResourceError
  | ConflictError
  | BusinessError
  | RateLimitError
  | ExternalServiceError
  | SystemError;

// Error Factory Functions
export const createValidationError = (
  subtype: ValidationErrorType,
  message: string,
  details?: Record<string, any>
): ValidationError => ({
  subtype,
  message,
  httpStatusCode: 400,
  timestamp: new Date().toISOString(),
  details,
});

export const createAuthenticationError = (
  subtype: AuthenticationErrorType,
  message: string,
  details?: Record<string, any>
): AuthenticationError => ({
  subtype,
  message,
  httpStatusCode: 401,
  timestamp: new Date().toISOString(),
  details,
});

export const createAuthorizationError = (
  subtype: AuthorizationErrorType,
  message: string,
  details?: Record<string, any>
): AuthorizationError => ({
  subtype,
  message,
  httpStatusCode: 403,
  timestamp: new Date().toISOString(),
  details,
});

export const createResourceError = (
  subtype: ResourceErrorType,
  message: string,
  details?: Record<string, any>
): ResourceError => ({
  subtype,
  message,
  httpStatusCode: 404,
  timestamp: new Date().toISOString(),
  details,
});

export const createConflictError = (
  subtype: ConflictErrorType,
  message: string,
  details?: Record<string, any>
): ConflictError => ({
  subtype,
  message,
  httpStatusCode: 409,
  timestamp: new Date().toISOString(),
  details,
});

export const createBusinessError = (
  subtype: BusinessErrorType,
  message: string,
  details?: Record<string, any>
): BusinessError => ({
  subtype,
  message,
  httpStatusCode: 402,
  timestamp: new Date().toISOString(),
  details,
});

export const createRateLimitError = (
  subtype: RateLimitErrorType,
  message: string,
  details?: Record<string, any>
): RateLimitError => ({
  subtype,
  message,
  httpStatusCode: 429,
  timestamp: new Date().toISOString(),
  details,
});

export const createExternalServiceError = (
  subtype: ExternalServiceErrorType,
  message: string,
  details?: Record<string, any>
): ExternalServiceError => ({
  subtype,
  message,
  httpStatusCode: 424,
  timestamp: new Date().toISOString(),
  details,
});

export const createSystemError = (
  subtype: SystemErrorType,
  message: string,
  details?: Record<string, any>
): SystemError => ({
  subtype,
  message,
  httpStatusCode: 500,
  timestamp: new Date().toISOString(),
  details,
});

// Type Guards
export const isValidationError = (error: AppError): error is ValidationError => 
  error.httpStatusCode === 400;

export const isAuthenticationError = (error: AppError): error is AuthenticationError => 
  error.httpStatusCode === 401;

export const isAuthorizationError = (error: AppError): error is AuthorizationError => 
  error.httpStatusCode === 403;

export const isResourceError = (error: AppError): error is ResourceError => 
  error.httpStatusCode === 404;

export const isConflictError = (error: AppError): error is ConflictError => 
  error.httpStatusCode === 409;

export const isBusinessError = (error: AppError): error is BusinessError => 
  error.httpStatusCode === 402;

export const isRateLimitError = (error: AppError): error is RateLimitError => 
  error.httpStatusCode === 429;

export const isExternalServiceError = (error: AppError): error is ExternalServiceError => 
  error.httpStatusCode === 424;

export const isSystemError = (error: AppError): error is SystemError => 
  error.httpStatusCode >= 500;

// Legacy Error Types (for backward compatibility)
export interface LegacyError {
  errorCode: number;
  errorMessage?: string;
}

// Conversion from Legacy to New Error Structure
export const convertLegacyError = (legacyError: LegacyError): AppError => {
  const { errorCode, errorMessage = 'An error occurred' } = legacyError;
  
  switch (errorCode) {
    case 400:
      return createValidationError('validation_missing', errorMessage);
    case 401:
      return createAuthenticationError('wrong_credentials', errorMessage);
    case 402:
      return createBusinessError('payment_failed', errorMessage);
    case 403:
      return createAuthorizationError('access_denied', errorMessage);
    case 404:
      return createResourceError('user_not_found', errorMessage);
    case 409:
      return createConflictError('email_already_registered', errorMessage);
    case 424:
      return createExternalServiceError('stripe_service_down', errorMessage);
    case 429:
      return createRateLimitError('too_many_requests', errorMessage);
    case 500:
    case 502:
    case 503:
    case 504:
      return createSystemError('internal_processing_error', errorMessage);
    default:
      return createSystemError('internal_processing_error', errorMessage);
  }
};
